import { RISK_TEXTS, stepHeading } from '@tabel/shared/sites/step-texts';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { Button, Modal, textareaCls } from '../../../components/ui';
import { CREATE, SITE } from '../texts';
import { useSite } from './actions';

/*
 * Окна страницы площадки (007 T040): «Продолжить с риском» с обязательной причиной (FR-015) и текст для копирования
 * вручную, если буфер обмена недоступен («Сведения для поддержки», значения подсказок).
 */

const REASON_MIN = 5;
const REASON_MAX = 500;

/** «Продолжить с риском»: причина 5–500 знаков, она попадёт в журнал действий. Только у шага с `warn`. */
export function RiskDialog({ step, onClose }: { step: number | null; onClose: () => void }) {
  const id = useId();
  const { act, busy } = useSite();
  const [reason, setReason] = useState('');
  const [error, setError] = useState(false);
  useEffect(() => {
    if (step === null) {
      setReason('');
      setError(false);
    }
  }, [step]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = reason.trim();
    if (text.length < REASON_MIN || text.length > REASON_MAX) {
      setError(true);
      return;
    }
    if (step !== null) void act.acceptRisk(step, text);
  };
  return (
    <Modal
      open={step !== null}
      onClose={onClose}
      title={RISK_TEXTS.title}
      description={step === null ? undefined : stepHeading(step)}
    >
      <form noValidate onSubmit={submit}>
        <label htmlFor={`${id}-reason`} className="mb-1.5 block text-[13px] font-medium text-ink">
          {RISK_TEXTS.reasonLabel}
        </label>
        <textarea
          id={`${id}-reason`}
          className={textareaCls}
          rows={3}
          maxLength={REASON_MAX}
          value={reason}
          aria-invalid={error || undefined}
          aria-describedby={`${id}-hint`}
          data-autofocus
          onChange={(e) => {
            setReason(e.target.value);
            setError(false);
          }}
        />
        <span id={`${id}-hint`} className={`mt-1 block text-[12px] ${error ? 'text-bad' : 'text-ink-3'}`}>
          {RISK_TEXTS.reasonHint}
        </span>
        <div className="mt-6 flex flex-wrap justify-end gap-2 max-sm:[&>button]:flex-auto">
          <Button onClick={onClose}>{CREATE.cancel}</Button>
          <Button type="submit" variant="primary" disabled={busy !== null}>
            {RISK_TEXTS.submit}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Буфер обмена недоступен: текст в поле, выделен — человек копирует его сам. */
export function CopyDialog({
  copy,
  onClose,
}: { copy: { title: string; text: string } | null; onClose: () => void }) {
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (copy) box.current?.select();
  }, [copy]);
  return (
    <Modal open={copy !== null} onClose={onClose} title={copy?.title ?? ''} description={SITE.copyFailed}>
      <textarea
        ref={box}
        readOnly
        rows={4}
        className={`${textareaCls} font-mono text-[13px]`}
        value={copy?.text ?? ''}
        aria-label={copy?.title ?? ''}
        data-autofocus
      />
    </Modal>
  );
}
